/**
 * File: assets/basari-buy-frame.js
 * Purpose: Frame-only add-to-cart on the default eyewear PDP.
 * Binds "Buy Frame Only" and the PDP cart icon ([data-basari-frame-cart]).
 * Reads the selected variant from [data-basari-product][data-variant-id].
 * Adds quantity 1 via Shopify Cart API with no line-item properties.
 * Reuses Dawn <cart-drawer>.renderContents() to update and open the drawer.
 * Loaded only by sections/basari-product-main.liquid.
 * Does not touch lens, VisionCare Pro, or contact-lens flows.
 */
(function () {
  'use strict';

  var BUTTON_SELECTOR = '[data-basari-buy-frame], [data-basari-frame-cart]';
  var ROOT_SELECTOR = '[data-basari-product]';
  var VARIANTS_JSON_SELECTOR = '[data-basari-variants-json]';
  var ERROR_SELECTOR = '[data-basari-buy-frame-error]';
  var LOADING_CLASS = 'is-loading';
  var READY_ATTR = 'data-basari-buy-frame-ready';
  var BUSY_ATTR = 'data-basari-buy-frame-busy';
  var ROOT_BUSY_ATTR = 'data-basari-frame-add-busy';

  function getCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  function parseVariantId(value) {
    var id = String(value || '').trim();
    if (!id || id === 'null' || id === 'undefined') return '';
    if (!/^\d+$/.test(id)) return '';
    return id;
  }

  function getVariantsConfig(root) {
    var node = root.querySelector(VARIANTS_JSON_SELECTOR);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  function findVariant(root, variantId) {
    var config = getVariantsConfig(root);
    var variants = config && config.variants ? config.variants : [];
    var i;

    for (i = 0; i < variants.length; i += 1) {
      if (String(variants[i].id) === String(variantId)) {
        return variants[i];
      }
    }

    return null;
  }

  function getErrorEl(button) {
    var describedBy;
    var wrap = button.parentElement;

    if (wrap) {
      var el = wrap.querySelector(ERROR_SELECTOR);
      if (el) return el;
    }

    describedBy = button.getAttribute('aria-describedby');
    return describedBy ? document.getElementById(describedBy) : null;
  }

  function getMessage(button, attr, fallback) {
    var value = button.getAttribute(attr);
    return value && value.trim() ? value : fallback;
  }

  function showError(button, message) {
    var errorEl = getErrorEl(button);
    if (!errorEl) return;
    errorEl.textContent = message || '';
    errorEl.hidden = false;
    errorEl.removeAttribute('hidden');
    button.setAttribute('aria-invalid', 'true');
  }

  function clearError(button) {
    var errorEl = getErrorEl(button);
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.hidden = true;
      errorEl.setAttribute('hidden', '');
    }
    button.removeAttribute('aria-invalid');
  }

  function setLoading(button, loading) {
    var root = button.closest(ROOT_SELECTOR);

    button.classList.toggle(LOADING_CLASS, loading);
    if (loading) {
      button.setAttribute(BUSY_ATTR, 'true');
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-disabled', 'true');
      if (root) root.setAttribute(ROOT_BUSY_ATTR, 'true');
    } else {
      button.removeAttribute(BUSY_ATTR);
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
      if (root) root.removeAttribute(ROOT_BUSY_ATTR);
    }
  }

  function isBusy(button) {
    var root = button.closest(ROOT_SELECTOR);

    return (
      (root && root.getAttribute(ROOT_BUSY_ATTR) === 'true') ||
      button.getAttribute(BUSY_ATTR) === 'true' ||
      button.getAttribute('aria-disabled') === 'true' ||
      button.classList.contains(LOADING_CLASS)
    );
  }

  function addFrameToCart(variantId, cart) {
    var routes = window.routes || {};
    var cartAddUrl = routes.cart_add_url || '/cart/add';
    var config =
      typeof window.fetchConfig === 'function'
        ? window.fetchConfig('javascript')
        : {
            method: 'POST',
            headers: { Accept: 'application/javascript', 'Content-Type': 'application/json' }
          };

    config.headers = config.headers || {};
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    var formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    if (cart && typeof cart.getSectionsToRender === 'function') {
      formData.append(
        'sections',
        cart.getSectionsToRender().map(function (section) {
          return section.id;
        })
      );
      formData.append('sections_url', window.location.pathname);
      if (typeof cart.setActiveElement === 'function') {
        cart.setActiveElement(document.activeElement);
      }
    }

    config.body = formData;

    return fetch(cartAddUrl, config).then(function (response) {
      return response.json().then(function (payload) {
        if (payload && payload.status) {
          var error = new Error(
            payload.description ||
              payload.message ||
              (window.cartStrings && window.cartStrings.error) ||
              'Unable to add to cart.'
          );
          error.payload = payload;
          throw error;
        }
        return payload;
      });
    });
  }

  function publishCartUpdate(variantId, payload) {
    if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
        source: 'basari-buy-frame',
        productVariantId: variantId,
        cartData: payload
      });
    }
  }

  function openExistingCartDrawer(cart, payload) {
    if (!cart || typeof cart.renderContents !== 'function') {
      throw new Error('Cart drawer is unavailable.');
    }

    if (cart.classList && cart.classList.contains('is-empty')) {
      cart.classList.remove('is-empty');
    }

    cart.renderContents(payload);
  }

  function onBuyFrameClick(event) {
    var button = event.currentTarget;
    var root;
    var variantId;
    var variant;
    var cart;

    event.preventDefault();
    event.stopPropagation();

    if (isBusy(button)) return;

    root = button.closest(ROOT_SELECTOR);
    if (!root || root.hasAttribute('data-basari-contact-lens')) return;

    clearError(button);

    variantId = parseVariantId(root.getAttribute('data-variant-id'));
    if (!variantId) {
      showError(
        button,
        getMessage(button, 'data-error-no-variant', 'Select a frame colour to continue.')
      );
      return;
    }

    variant = findVariant(root, variantId);
    if (!variant || variant.available !== true) {
      showError(
        button,
        getMessage(button, 'data-error-unavailable', 'This frame colour is currently unavailable.')
      );
      return;
    }

    cart = getCartDrawer();
    if (!cart || typeof cart.renderContents !== 'function') {
      showError(
        button,
        getMessage(button, 'data-error-cart', 'Could not add this frame to your cart. Please try again.')
      );
      return;
    }

    setLoading(button, true);

    addFrameToCart(variantId, cart)
      .then(function (payload) {
        publishCartUpdate(variantId, payload);
        openExistingCartDrawer(cart, payload);
      })
      .catch(function (error) {
        showError(
          button,
          (error && error.message) ||
            getMessage(button, 'data-error-cart', 'Could not add this frame to your cart. Please try again.')
        );
      })
      .finally(function () {
        setLoading(button, false);
      });
  }

  function bindButton(button) {
    var root;
    if (!button || button.getAttribute(READY_ATTR) === 'true') return;

    root = button.closest(ROOT_SELECTOR);
    if (root && root.hasAttribute('data-basari-contact-lens')) return;

    button.setAttribute(READY_ATTR, 'true');
    button.addEventListener('click', onBuyFrameClick);

    if (document.querySelector('cart-drawer')) {
      button.setAttribute('aria-haspopup', 'dialog');
    }

    if (root) {
      root.addEventListener('basari:variant:change', function () {
        clearError(button);
      });
    }
  }

  function initAll(scope) {
    var root = scope || document;
    var buttons = root.querySelectorAll ? root.querySelectorAll(BUTTON_SELECTOR) : [];
    var i;

    for (i = 0; i < buttons.length; i += 1) {
      bindButton(buttons[i]);
    }

    if (root.matches && root.matches(BUTTON_SELECTOR)) {
      bindButton(root);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target) initAll(event.target);
  });
})();
